import React, { useCallback, useState } from 'react';
import { Upload, FileUp, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useSupabaseUpload } from '../hooks/useSupabaseUpload';
import { useProjectStore } from '../stores/useProjectStore';

const ACCEPTED_TYPES = ['.las', '.laz', '.tif', '.tiff', '.asc', '.csv'];

export const UploadPanel: React.FC = () => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dismissedError, setDismissedError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const setActiveScene = useProjectStore((s) => s.setActiveScene);

  const uploadMutation = useSupabaseUpload(activeProjectId);

  // Clear success toast when mutation resets
  React.useEffect(() => {
    if (uploadMutation.isSuccess) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [uploadMutation.isSuccess]);

  // Reset dismissed error when a new error occurs
  React.useEffect(() => {
    if (uploadMutation.error) {
      setDismissedError(false);
    }
  }, [uploadMutation.error]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const processFile = useCallback(
    (file: File) => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ACCEPTED_TYPES.includes(ext)) {
        // eslint-disable-next-line no-console
        console.error(`Unsupported file type: ${ext}. Accepted: ${ACCEPTED_TYPES.join(', ')}`);
        return;
      }
      uploadMutation.mutate(
        { file },
        {
          onSuccess: ({ sceneId }) => {
            setActiveScene(sceneId);
          },
        },
      );
    },
    [uploadMutation, setActiveScene],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        processFile(file);
      }
      e.target.value = '';
    },
    [processFile],
  );

  const isUploading = uploadMutation.isPending;
  const uploadError = uploadMutation.error && !dismissedError
    ? uploadMutation.error.message
    : null;

  const noProject = !activeProjectId;

  return (
    <div className="flex flex-col gap-3 p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
        Import Data
      </h2>

      {noProject && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-900/30 p-3 text-sm text-amber-200 border border-amber-800/50">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">Select a project to upload files.</span>
        </div>
      )}

      {uploadError && (
        <div className="flex items-start gap-2 rounded-lg bg-red-900/40 p-3 text-sm text-red-200 border border-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{uploadError}</span>
          <button
            onClick={() => setDismissedError(true)}
            className="shrink-0 rounded p-0.5 hover:bg-red-900/60 min-w-touch min-h-touch flex items-center justify-center"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {showSuccess && !isUploading && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-900/40 p-3 text-sm text-emerald-200 border border-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>Upload complete</span>
        </div>
      )}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={[
          'relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 transition-colors',
          'min-h-[160px]',
          noProject
            ? 'border-slate-700 bg-slate-800/30 opacity-50'
            : isDragOver
              ? 'border-sky-400 bg-sky-900/20'
              : 'border-slate-600 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800/70',
          (isUploading || noProject) && 'pointer-events-none',
        ].join(' ')}
      >
        <input
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleFileInput}
          className="absolute inset-0 cursor-pointer opacity-0"
          disabled={isUploading || noProject}
        />

        {isUploading ? (
          <>
            <FileUp className="h-8 w-8 text-sky-400 animate-pulse" />
            <div className="text-sm font-medium text-sky-300">Uploading…</div>
            <div className="h-1.5 w-full max-w-[200px] overflow-hidden rounded-full bg-slate-700">
              <div className="h-full w-2/3 rounded-full bg-sky-400 animate-pulse" />
            </div>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 text-slate-400" />
            <div className="text-center">
              <p className="text-sm font-medium text-slate-300">
                {noProject ? 'Select a project first' : 'Drop file or click to browse'}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {ACCEPTED_TYPES.join(', ')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
